<template>
  <div class="card-segments">
    <Carousel
      ref="carouselRef"
      :opts="carouselOptions"
      class="card-segments__carousel"
      @init-api="onInitApi"
    >
      <CarouselContent>
        <CarouselItem
          v-for="(seg, index) in segments"
          :key="seg.start_time"
          :click-to-index="true"
          class="card-segments__slide"
        >
          <div
            class="card-segments__item"
            :class="{ 'card-segments__item--active': activeIndex === index }"
            @click="handleClick(seg.start_time)"
          >
            <img
              class="card-segments__image"
              :src="transformImage(seg.image_url, { height: 45 }).replace('_png', '_jpg')"
            />
          </div>
        </CarouselItem>
      </CarouselContent>
      <div
        v-if="canScrollPrev"
        class="card-segments__arrow card-segments__arrow--left"
        @click="scrollPrev"
      >
        <IconMoreRight class="card-segments__icon card-segments__icon--left" />
      </div>
      <div
        v-if="canScrollNext"
        class="card-segments__arrow card-segments__arrow--right"
        @click="scrollNext"
      >
        <IconMoreRight class="card-segments__icon card-segments__icon--right" />
      </div>
    </Carousel>
  </div>
</template>

<script setup lang="ts">
import Carousel from '@rendora/design/components/carousel/Carousel.vue';
import CarouselContent from '@rendora/design/components/carousel/CarouselContent.vue';
import CarouselItem from '@rendora/design/components/carousel/CarouselItem.vue';
import type { CarouselApi, CarouselOptions } from '@rendora/design/components/carousel/interface';
import IconMoreRight from '~/design/icons/more-right.svg?component';
import { transformImage } from '~/utils';

interface Segment {
  start_time: number;
  image_url: string;
}

const props = withDefaults(
  defineProps<{
    segments?: Segment[];
  }>(),
  {
    segments: () => [],
  },
);

const carouselRef = ref<InstanceType<typeof Carousel>>();
const carouselApi = ref<CarouselApi>();
const currentTime = defineModel<number>('currentTime', { required: true });

const activeIndex = computed(() => {
  for (let i = 0; i < props.segments.length; i++) {
    const next = props.segments[i + 1];
    if (!next || currentTime.value < next.start_time) {
      return i;
    }
  }
  return 0;
});

const carouselOptions = computed(
  () =>
    ({
      slidesPerView: 'auto',
      spaceBetween: 16,
      loop: false,
    }) as CarouselOptions,
);

const canScrollPrev = computed(() => carouselApi.value?.canScrollPrev() ?? false);
const canScrollNext = computed(() => carouselApi.value?.canScrollNext() ?? false);

const onInitApi = (api: CarouselApi) => {
  carouselApi.value = api;
};

const goTo = (index: number) => {
  carouselApi.value?.scrollTo(index);
};

const handleClick = (time: number) => {
  currentTime.value = time;
};

const scrollPrev = () => {
  carouselApi.value?.scrollPrePage();
};

const scrollNext = () => {
  carouselApi.value?.scrollNextPage();
};

const reset = () => {
  carouselApi.value?.scrollTo(0);
};

watch(activeIndex, index => goTo(index));

watch(
  () => props.segments,
  () => reset(),
);
</script>

<style scoped lang="scss">
.card-segments {
  position: relative;
}

.card-segments__carousel {
  position: relative;
}

.card-segments__slide {
  flex: 0 0 auto;
  width: clamp(160px, 160px + (100vw - 1600px) * 0.75, 213px);
}

.card-segments__item {
  display: flex;
  width: clamp(160px, 160px + (100vw - 1600px) * 0.75, 213px);
  aspect-ratio: 16/9;
  border-radius: 8px;
  padding: 1px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
}

.card-segments__item::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  border: 1px solid transparent;
  border-radius: 6px;
  pointer-events: none;
  transition: border-color 0.2s ease;
  z-index: 2;
}

.card-segments__item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  pointer-events: none;
  transition: border-color 0.2s ease;
  z-index: 1;
}

.card-segments__item:hover::before {
  border-color: #fff;
}

.card-segments__item:hover::after {
  border-color: var(--x-purple-6);
}

.card-segments__item--active::before {
  border-color: #fff;
}

.card-segments__item--active::after {
  border-color: var(--x-purple-6);
}

.card-segments__image {
  height: 100%;
  width: 100%;
  border-radius: 8px;
  background-color: var(--x-back-3);
  object-fit: contain;
}

.card-segments__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  color: var(--x-text-3);
  border: 1px solid var(--x-back-3);
  background: var(--x-back-1);
  transition: all 0.1s ease;
  box-shadow: var(--x-shadow);
}

.card-segments__arrow:hover {
  background-color: var(--x-back-5);
  border-color: var(--x-back-3);
}

.card-segments__arrow--left {
  left: -16px;
}

.card-segments__arrow--right {
  right: -16px;
}

.card-segments__icon {
  width: 14px;
  height: 14px;
  color: var(--x-text-3);
}

.card-segments__icon--left {
  transform: rotate(180deg);
}
</style>
